import { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AlertModal from "../../../../components/alertModal/alertModal";
import CustomBreadcrumb from "../../../../components/customBreadcrumb/CustomBreadcrumb";
import MainLoader from "../../../../components/loading/MainLoader";
import SelectOption, {
  ISelectOption,
} from "../../../../components/selectOption/SelectOption";
import TextInput from "../../../../components/textInput/TextInput";
import Title from "../../../../components/title/Title";
import { IRoleData } from "../../../../interfaces/roleData";
import { ISuccessState } from "../../../../interfaces/successState";
import { updateExternalUser } from "../../../../services/externalUser";
import {
  addExternalUser,
  getExternalUserById,
  getExternalUserRole,
} from "../../../../store/actions/externalUser.action";
import { IRootState } from "../../../../store/configureStore";
import { IDataBundleUser } from "../../../../interfaces/dataBundleUser";

export interface IAddUserFormInput {
  userId?: number;
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  // email: string;
  contactNumber: string;
  role: string;
  status: boolean;
  adminUser?: number;
  customer?: number;
  nickName: string;
  title: string;
}

const userStatus: { name: string; id: number }[] = [
  { name: "Active", id: 1 },
  { name: "Inactive", id: 0 },
];

const titleStatus: { name: string; id: string }[] = [
  { name: "Mr.", id: "Mr" },
  { name: "Ms.", id: "Ms" },
  { name: "Mrs.", id: "Mrs" },
  { name: "Miss.", id: "Miss" },
];

const AddUser = () => {
  const { userId } = useParams();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [isSuccess, setIsSuccess] = useState<ISuccessState | undefined>();

  const {
    register,
    control,
    handleSubmit,
    trigger,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IAddUserFormInput>();

  const {
    loadingRoleData,
    roleDataBundle,
    user,

    creatingUser,
    loadingDataObject,
    userByRoleId,
  } = useSelector(({ externalUser, privileges }: IRootState) => ({
    loadingRoleData: externalUser.loadingRoleData,
    roleDataBundle: externalUser.roleDataBundle,
    user: privileges.dataBundleUser,
    creatingUser: externalUser.creatingUser,
    loadingDataObject: externalUser.loadingDataObject,
    userByRoleId: externalUser.dataObject,
  }));

  const [selectedRoleValue, setSelectedRoleValue] = useState<
    ISelectOption | []
  >([]);
  const [selectedTitleValue, setSelectedTitleValue] = useState<
    ISelectOption | []
  >([]);
  const [selectedStatusValue, setSelectedStatusValue] = useState<ISelectOption>(
    {
      label: "Active",
      value: 1,
    }
  );

  const Password = watch("password");

  const UserName = watch("username");

  const contactNumber = watch("contactNumber");

  useEffect(() => {
    if (Password) {
      trigger("password");
    }
  }, [Password, trigger]);

  useEffect(() => {
    if (UserName) {
      trigger("username");
    }
  }, [UserName, trigger]);

  useEffect(() => {
    if (contactNumber) {
      trigger("contactNumber");
    }
  }, [contactNumber, trigger]);

  useEffect(() => {
    dispatch(getExternalUserRole());
    if (user && userId) {
      dispatch(getExternalUserById(+userId, user.id));
    }
  }, [dispatch, userId, user]);

  useEffect(() => {
    if (userId && userByRoleId) {
      setValue("firstName", userByRoleId.name.split(/\s+/)[0]);
      setValue("lastName", userByRoleId.name.split(/\s+/)[1]);
      setValue("contactNumber", userByRoleId.contactNumber);
      setValue("role", userByRoleId.role.id + "");
      setValue("status", userByRoleId.active ?? true);
      setValue("nickName", userByRoleId.nickName);
      setValue("title", userByRoleId.title);

      const roleData: IRoleData | undefined = roleDataBundle.find(
        (data) => data.id === userByRoleId.role.id
      );
      const statusData = userStatus.find(
        (status) => status.id === (userByRoleId.active ? 1 : 0)
      );
      const titleData = titleStatus.find(
        (status) => status.id === userByRoleId.title
      );

      if (roleData) {
        setSelectedRoleValue({
          label: roleData.name,
          value: roleData.id,
        });
      }

      if (statusData) {
        setSelectedStatusValue({
          label: statusData.name,
          value: statusData.id,
        });
      }

      if (titleData) {
        setSelectedTitleValue({
          label: titleData.name,
          value: titleData.id,
        });
      }
    }
  }, [userByRoleId, setValue, roleDataBundle, userId]);

  const onSubmit: SubmitHandler<IAddUserFormInput> = (data) => {
    if (!user) {
      return;
    }

    data = {
      ...data,
      adminUser: user?.id,
      customer: user?.customer.id,
    };

    if (typeof data.status === "number") {
      data.status = data.status === 1;
    }

    if (!userId) {
      dispatch(addExternalUser(data, setIsSuccess));
    } else {
      data.userId = +userId;
      updateExternalUser(data)
        .then(() => {
          setIsSuccess({
            isSuccess: true,
            message: "User updated successfully!",
          });
        })
        .catch((error) => {
          setIsSuccess({
            isSuccess: false,
            message: error.message,
          });
        });
    }
  };

  return (
    <div className="page-container">
      <MainLoader show={loadingRoleData || creatingUser || loadingDataObject} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/settings/users", title: "Manage Users" },
          {
            path: "/settings/users/add",
            title: userId ? "Update User" : "Add User",
          },
        ]}
      />

      <Title>{userId ? "Update" : "Add"} User</Title>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row className="mt-4 row-cols-1 row-cols-md-3 gy-3">
          <Col>
            <Controller
              control={control}
              defaultValue={undefined}
              name="title"
              rules={{ required: true }}
              render={({ field: { onChange } }) => (
                <SelectOption
                  label="Title"
                  onChange={(val: any) => {
                    setSelectedTitleValue(val);
                    onChange(val.value);
                  }}
                  required
                  options={[
                    { label: "Mr.", value: "Mr" },
                    { label: "Ms.", value: "Ms" },
                    { label: "Mrs.", value: "Mrs" },
                    { label: "Miss.", value: "Miss" },
                  ]}
                  value={selectedTitleValue}
                  errors={[
                    {
                      isShow: errors.title?.type === "required",
                      message: "Title is required",
                    },
                  ]}
                />
              )}
            />
          </Col>

          <Col>
            <TextInput
              label="First Name"
              required
              color="gray"
              textAlign="left"
              useFormRegister={{ ...register("firstName", { required: true }) }}
              errors={[
                {
                  isShow: errors.firstName?.type === "required",
                  message: "First Name is required",
                },
              ]}
            />
          </Col>
          <Col>
            <TextInput
              label="Last Name"
              color="gray"
              textAlign="left"
              useFormRegister={{ ...register("lastName") }}
            />
          </Col>
          {!userId && (
            <>
              <Col>
                <TextInput
                  label="Username (email)"
                  required
                  color="gray"
                  textAlign="left"
                  useFormRegister={{
                    ...register("username", {
                      required: true,
                      pattern:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    }),
                  }}
                  value={watch("username")}
                  errors={[
                    {
                      isShow: errors.username?.type === "required",
                      message: "Username is required",
                    },
                    {
                      isShow: errors.username?.type === "pattern",
                      message: "User name is invalid",
                    },
                  ]}
                />
              </Col>
              <Col>
                <TextInput
                  label="Password"
                  required
                  textAlign="left"
                  color="gray"
                  type="password"
                  useFormRegister={{
                    ...register("password", {
                      required: true,
                      minLength: 8,
                    }),
                  }}
                  value={watch("password")}
                  errors={[
                    {
                      isShow: errors.password?.type === "required",
                      message: "Password is required",
                    },
                    {
                      isShow: errors.password?.type === "minLength",
                      message: "Password must have 8 characters",
                    },
                  ]}
                />
              </Col>
              {/* <Col>
                <TextInput
                  label="Email"
                  required
                  textAlign="left"
                  color="gray"
                  type="email"
                  useFormRegister={{
                    ...register("email", {
                      required: true,
                      pattern:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    }),
                  }}
                  errors={[
                    {
                      isShow: errors.email?.type === "required",
                      message: "Email is required",
                    },
                    {
                      isShow: errors.email?.type === "pattern",
                      message: "Email is invalid",
                    },
                  ]}
                />
              </Col> */}
            </>
          )}
          <Col>
            <TextInput
              label="Contact Number"
              required
              color="gray"
              textAlign="left"
              useFormRegister={{
                ...register("contactNumber", {
                  required: true,
                  pattern: /^[0][7][01245678][0-9]{7}$/gm,
                }),
              }}
              maxLength={10}
              value={watch("contactNumber")}
              type="tel"
              inputMode="numeric"
              errors={[
                {
                  isShow: errors.contactNumber?.type === "required",
                  message: "Contact Number is required",
                },
                {
                  isShow: errors.contactNumber?.type === "pattern",
                  message:
                    "Contact Number must be 10 digits and start with 07XXXXXXXX",
                },
              ]}
            />
          </Col>
          <Col>
            <Controller
              control={control}
              defaultValue={undefined}
              name="role"
              rules={{ required: true }}
              render={({ field: { onChange } }) => (
                <SelectOption
                  disabled={user?.id == userId}
                  label="Role"
                  onChange={(val: any) => {
                    setSelectedRoleValue(val);
                    onChange(val.value);
                  }}
                  required
                  // options={roleDataBundle.map((item: IRoleData) => ({
                  //   label: item.name,
                  //   value: item.id,
                  // }))}
                  options={roleDataBundle
                    .filter(function (el) {
                      return el.code !== "USER_ADMIN";
                    })
                    .map(function (role) {
                      return { label: role.name, value: role.id };
                    })}
                  value={selectedRoleValue}
                  errors={[
                    {
                      isShow: errors.role?.type === "required",
                      message: "Role is required",
                    },
                  ]}
                />
              )}
            />
          </Col>
          <Col>
            <Controller
              control={control}
              defaultValue={true}
              name="status"
              rules={{ required: true }}
              render={({ field: { onChange } }) => (
                <SelectOption
                  disabled={user?.id == userId}
                  label="Status"
                  onChange={(val: any) => {
                    setSelectedStatusValue(val);
                    onChange(val.value);
                  }}
                  required
                  options={userStatus.map((item) => ({
                    label: item.name,
                    value: item.id,
                  }))}
                  value={selectedStatusValue}
                  errors={[
                    {
                      isShow: errors.status?.type === "required",
                      message: "Status is required",
                    },
                  ]}
                />
              )}
            />
          </Col>
          <Col>
            <TextInput
              label="Nick Name"
              color="gray"
              textAlign="left"
              useFormRegister={{ ...register("nickName") }}
              // errors={[
              //   {
              //     isShow: errors.firstName?.type === "required",
              //     message: "Nick Name is required",
              //   },
              // ]}
            />
          </Col>
        </Row>
        <Row className="mt-5 justify-content-center">
          <Col className="col-md-3">
            <Button type="submit" className="w-100" variant="success">
              Submit
            </Button>
          </Col>
        </Row>
      </form>

      <AlertModal
        show={!!isSuccess}
        buttons={[
          {
            variant: "success",
            text: "OK",
            onClick: () => {
              if (isSuccess?.isSuccess) {
                reset();
                setSelectedRoleValue([]);
                navigate(-1);
                // setSelectedStatusValue([]);
              }
              setIsSuccess(undefined);
            },
          },
        ]}
        variant={isSuccess?.isSuccess ? "success" : "error"}
      >
        {isSuccess?.message}
      </AlertModal>
    </div>
  );
};

export default AddUser;
